import React, { useContext } from 'react';

import { type EgressFilteringService } from '@forge/egress';
import type ApolloClient from 'apollo-client';

import type { DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { VisitorCollection } from '@atlaskit/adf-utils/types';
import { useMediaClient } from '@atlaskit/media-client-react';
import { fg } from '@atlaskit/platform-feature-flags';
import { ReactRenderer, type RendererProps } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import {
	type BridgeConfig,
	EgressErrorMessage,
	ForgeUIRenderer,
	type GetContextTokenFn,
	isValidImageUrl,
	RendererContext,
	sanitizeUrl,
	useEgressPermissionService,
	useNavigation,
} from '@atlassian/forge-ui';
import type { RenderFn } from '@atlassian/forge-ui-types';

import { ConnectMacroRenderer } from './ConnectMacroRenderer';

interface Dependencies {
	bridge?: BridgeConfig;
	client?: ApolloClient<any>;
	getContextToken?: GetContextTokenFn;
	extensionData?: {
		[key: string]: unknown;
		content?: { id?: string; subtype?: string | null; type?: string; };
		references?: unknown[];
		space?: { id?: string; key?: string; };
		type?: string;
	};
}

// Keep in sync with platform/packages/forge/forge-react-types/src/components/adfrenderer/index.ts
export type AdfRendererProps = {
	/**
	 * An ADF document to render
	 *
	 * @type [DocNode](https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/doc/)
	 */
	document: DocNode;

	// documentWithoutMedia is only used internally on the Forge side, this is not part of the public API
	documentWithoutMedia?: DocNode;

	/**
	 * A function to determine behaviour for handling unsupported nodes:
	 * - Return a new Node to replace the unsupported one
	 * - Return false to remove the node entirely
	 * - Return undefined to leave the node as-is (default behaviour)
	 *
	 * @type [Visitor](https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/master/editor/adf-utils/src/types/index.ts)
	 */
	replaceUnsupportedNode?: Function;
};

type AdfRendererInternalProps = AdfRendererProps & {
	eventHandlers: RendererProps['eventHandlers'];
};

const filterInvalidMediaUrls = (
	appDomainName: string | undefined,
	egressServices: EgressFilteringService[],
	document: DocNode,
): {
	filteredDocument: DocNode;
	invalidUrls: string[];
} => {
	const invalidUrls = new Set<string>();

	const visitors: VisitorCollection = {
		media: (node) => {
			const url = node?.attrs?.url;
			if (url && !egressServices.some((service) => isValidImageUrl(url, service, appDomainName))) {
				invalidUrls.add(url);
				return {
					...node,
					attrs: {
						...node.attrs,
						// Remove the URL so it fails to load
						// We use a space because an empty string causes an infinite loading spinner
						url: ' ',
					},
				};
			}
		},
	};

	const filteredDocument = traverse(document, visitors);

	return {
		filteredDocument: filteredDocument ? (filteredDocument as DocNode) : document,
		invalidUrls: Array.from(invalidUrls),
	};
};

const useSupportsMedia = () => {
	try {
		useMediaClient();
		return true;
		// eslint-disable-next-line no-unused-vars
	} catch (e) {
		// Throws if no MediaClientProvider is found
		return false;
	}
};


export const makeADFRenderer = ({ client, bridge, getContextToken, extensionData }: Dependencies) => {
	return (props: Parameters<RenderFn>[0]): React.JSX.Element => (
		<AdfRenderer client={client} bridge={bridge} getContextToken={getContextToken} extensionData={extensionData} {...props} />
	);
};

/**
 * The AdfRenderer component provides a way to render a valid ADF document, using the same renderer that Atlassian uses internally to render ADF content in Confluence pages, Jira work items, and so on.
 * It allows you to replace node types that are unsupported in the context of a Forge app with replacement content, or remove them entirely.
 * See [Atlassian Document Format](/cloud/jira/platform/apis/document/structure/) for information on valid nodes.
 *
 * This component uses [@atlaskit/renderer](https://www.npmjs.com/package/@atlaskit/renderer) under the hood.
 *
 * Visit [Renderer editor](https://atlaskit.atlassian.com/examples/editor/renderer/basic) for a comprehensive list of different ADF document examples
 */
export const AdfRenderer = (props: Parameters<RenderFn>[0] & Dependencies): React.JSX.Element => {
	const supportsMedia = useSupportsMedia();
	const { egress, properties, appDomainName, forgeEnvironment } = useContext(RendererContext);
	const imagesEgressService = useEgressPermissionService('IMAGES', egress);
	const mediaEgressService = useEgressPermissionService('MEDIA', egress);
	const egressServices = [imagesEgressService, mediaEgressService];

	const client = props.client;
	const bridge = props.bridge;

	const getContextToken = props.getContextToken;
	const extensionData = props.extensionData;

	const { onNavigate, getModalJsx } = useNavigation({ extension: { egress, properties } });

	const { document, documentWithoutMedia, ...adfRendererProps } = props.forgeDoc
		.props as AdfRendererInternalProps;

	// The client provides both the document with media and without media, as it doesn't know whether the MediaClientProvider is present
	// Then, in the parent product, we can decide whether to render the document with media or without media
	const doc = !supportsMedia && documentWithoutMedia ? documentWithoutMedia : document;

	if (!doc && fg('platform-ui-kit-adf-renderer-require-document')) {
		return <></>;
	}

	const { filteredDocument, invalidUrls } = filterInvalidMediaUrls(
		appDomainName,
		egressServices,
		doc,
	);

	return (
		<>
			{forgeEnvironment === 'DEVELOPMENT' &&
				invalidUrls.map((url) => <EgressErrorMessage key={url} url={url} />)}
			{getModalJsx()}
			<RendererActionsContext>
				<ReactRenderer
					{...adfRendererProps}
					eventHandlers={{
						link: {
							onClick: (e) => {
								e.preventDefault();
								adfRendererProps?.eventHandlers?.link?.onClick?.(e, e.currentTarget.href);
								onNavigate({ type: 'same-tab', url: sanitizeUrl(e.currentTarget.href) });
							},
						},
					}}
					document={filteredDocument}
					extensionHandlers={
						client && fg('platform_add_embedded_macro_support_to_ui_kit')
							? {
									'com.atlassian.ecosystem': (props) => {
										const { localId, parameters } = props;
										const { extension, extensionData, accountId, cloudId, contextIds } =
											parameters.extensionProperties;

										// Extract config from extension parameters (can be in guestParams or config)
										const config = parameters.guestParams || parameters.config;

										const autoConvertLink = parameters?.autoConvertLink;

										const augmentedExtensionData = {
											...extensionData,
											autoConvertLink,
										};

										// ForgeUIRenderer will fetch fresh extension data from XLS when isEmbeddedMacro is true
										return (
											<ForgeUIRenderer
												accountId={accountId}
												cloudId={cloudId}
												client={client}
												contextIds={contextIds}
												extension={extension}
												extensionData={augmentedExtensionData}
												// Pass config via extensionPayload so it's available in context.extension.config
												extensionPayload={config ? { config } : undefined}
												localId={localId}
												product={'confluence'}
												// Only required for bodied macros. Since we are currently not supporting embedded bodied macros, this should be empty.
												extensionViewData={{}}
												// Pass the same bridge so embedded macro can communicate with product
												bridge={bridge}
												getContextToken={getContextToken}
												isEmbeddedMacro={true}
											/>
										);
									},
									// Handler for Connect macros (including native Confluence macros like TOC, Chart, Details, etc.)
									'com.atlassian.confluence.macro.core': (props) => {
										const { localId, parameters } = props;
										const extensionKey = props.extensionKey;

										// Get contentId from the Forge extension's context (extensionData) first,
										// which is the page containing the Forge app.
										// Fallback to _parentId for cross-page macros (e.g., include-page macro).
										// See: confluence/next/packages/fabric-extension-legacy-macro-renderer/src/LegacyMacroRendererQuery.tsx
										const contentId =
											extensionData?.content?.id || parameters?.macroParams?._parentId?.value || '';

										// Build extension object for ConnectMacroRenderer
										const connectExtension = {
											extensionType: 'com.atlassian.confluence.macro.core',
											extensionKey,
											localId,
											parameters: {
												macroParams: parameters?.macroParams,
												macroMetadata: parameters?.macroMetadata,
												guestParams: parameters?.guestParams,
											},
										};

										return (
											<ConnectMacroRenderer
												client={client}
												extension={connectExtension}
												contentId={contentId}
											/>
										);
									},
							  }
							: undefined
					}
				/>
			</RendererActionsContext>
		</>
	);
};
